import { processListingParams } from '@/api/helpers';
import { ListAccountsRequest, ListAccountsResponse } from '@/client/accounts';
import {
  AccountsSchedulesRequest,
  BranchesSchedulesRequest,
  ChangeScheduleDateRequest,
  ChangeScheduleEnrollTypeRequest,
  Course,
  CourseChanges,
  CourseEntityScheduleEnum,
  CourseUsersSummary,
  CourseUsersSummaryRequest,
  DeleteEnrollmentPayload,
  ForceCompleteRequest,
  GroupsSchedulesRequest,
  HighlightedCourse,
  ListAccountCoursesRequest,
  ListAccountScheduleRequest,
  ListAccountScheduleResponse,
  ListCourseAccountRequest,
  ListCourseAccountResponse,
  ListCourseBranchesRequest,
  ListCourseBranchesResponse,
  ListCourseGroupsRequest,
  ListCourseGroupsResponse,
  ListCourseScheduleRequest,
  ListCourseScheduleResponse,
  ListCoursesRequest,
  ListCoursesResponse,
  ListCourseUsersRequest,
  ListCourseUsersResponse,
  MakeAvailableAccountsRequest,
  MakeAvailableCoursesToAccountRequest,
  MigrateCourseMode,
  MigrateCourseResponse,
  Resource,
  SaveCourseRequest,
  UpdateCourseRequest,
  UsersSchedulesRequest,
} from '@/client/courses';
import { getExceptionFromAxiosError } from '@/client/helpers';
import { LearnerCourseDispatchResponse } from '@/client/learner-course';
import { MessageResponseModel } from '@/client/models';
import { AxiosError, AxiosInstance, AxiosResponse } from 'axios';
import { Quiz } from '../quizzes';
import { MessageResponse } from '../types';
import { CustomizeCourseRequest } from './types/course-customization.type';
import { SlideDeck } from './types/courses.type';

export const coursesService = (client: AxiosInstance) => {
  const ACCOUNT_COURSES_BASE_URL = (accountId: string) =>
    `accounts/${accountId}/courses`;
  const COURSES_BASE_URL = '/courses';

  const getCourses = async (
    params: ListCoursesRequest,
  ): Promise<ListCoursesResponse> => {
    try {
      const result = await client.get<
        ListCoursesResponse,
        AxiosResponse<ListCoursesResponse>
      >(COURSES_BASE_URL, {
        withCredentials: true,
        params: processListingParams(params),
      });

      return result.data;
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const getAccountCourses = async (
    params: ListAccountCoursesRequest,
    accountId: string,
  ): Promise<ListCoursesResponse> => {
    try {
      const result = await client.get<
        ListCoursesResponse,
        AxiosResponse<ListCoursesResponse>
      >(ACCOUNT_COURSES_BASE_URL(accountId), {
        withCredentials: true,
        params: processListingParams(params),
      });

      return result.data;
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const getCourse = async (courseId?: string): Promise<Course> => {
    try {
      const result = await client.get<Course, AxiosResponse<Course>>(
        `${COURSES_BASE_URL}/${courseId}`,
        {
          withCredentials: true,
        },
      );

      return result.data;
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const saveCourse = async (request: SaveCourseRequest): Promise<Course> => {
    try {
      const result = await client.post<Course, AxiosResponse<Course>>(
        COURSES_BASE_URL,
        request,
        {
          withCredentials: true,
        },
      );

      return result.data;
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const updateCourse = async (
    request: UpdateCourseRequest,
  ): Promise<Course> => {
    try {
      const result = await client.patch<Course, AxiosResponse<Course>>(
        `${COURSES_BASE_URL}/${request.courseId}`,
        request.updates,
        {
          withCredentials: true,
        },
      );

      return result.data;
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const deleteCourse = async (
    courseId: string,
  ): Promise<MessageResponseModel> => {
    try {
      const result = await client.delete<
        MessageResponseModel,
        AxiosResponse<MessageResponseModel>
      >(`${COURSES_BASE_URL}/${courseId}`, {
        withCredentials: true,
      });

      return new MessageResponseModel(result.data);
    } catch (e) {
      throw e as AxiosError;
    }
  };

  const getCourseUrl = async (
    scormCourseId: string,
    id: string,
  ): Promise<LearnerCourseDispatchResponse> => {
    try {
      const result = await client.get<
        LearnerCourseDispatchResponse,
        AxiosResponse<LearnerCourseDispatchResponse>
      >(`${COURSES_BASE_URL}/${scormCourseId}/files/${id}/dispatch-url`, {
        withCredentials: true,
      });

      return result.data;
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const getResourceDispatchUrl = async (
    courseId: string,
    resourceId: string,
    language: string,
    signal?: AbortSignal,
  ): Promise<LearnerCourseDispatchResponse> => {
    try {
      const result = await client.get<
        LearnerCourseDispatchResponse,
        AxiosResponse<LearnerCourseDispatchResponse>
      >(
        `${COURSES_BASE_URL}/${courseId}/resources/${resourceId}/translations/${language}/dispatch-url`,
        {
          withCredentials: true,
          signal,
        },
      );

      return result.data;
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const saveAvailableAccounts = async (
    request: MakeAvailableAccountsRequest,
  ): Promise<MessageResponseModel> => {
    try {
      const result = await client.post<
        MessageResponseModel,
        AxiosResponse<MessageResponseModel>
      >(
        `${COURSES_BASE_URL}/${request.courseId}/availability/accounts`,
        { accounts: request.accounts },
        {
          withCredentials: true,
        },
      );

      return result.data;
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const getUnavailableCoursesToAccount = async (
    params: ListCoursesRequest,
    accountId: string,
  ): Promise<ListCoursesResponse> => {
    try {
      const result = await client.get<
        ListCoursesResponse,
        AxiosResponse<ListCoursesResponse>
      >(`accounts/${accountId}/unavailable-courses`, {
        withCredentials: true,
        params: processListingParams(params),
      });

      return result.data;
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const saveAvailableCoursesToAccount = async (
    request: MakeAvailableCoursesToAccountRequest,
  ): Promise<MessageResponseModel> => {
    try {
      const result = await client.post<
        MessageResponseModel,
        AxiosResponse<MessageResponseModel>
      >(
        `${COURSES_BASE_URL}/availability/accounts/${request.accountId}`,
        { courses: request.courses },
        {
          withCredentials: true,
        },
      );

      return result.data;
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const saveUnavailableAccounts = async (
    request: MakeAvailableAccountsRequest,
  ): Promise<MessageResponseModel> => {
    try {
      const result = await client.delete<
        MessageResponseModel,
        AxiosResponse<MessageResponseModel>
      >(`${COURSES_BASE_URL}/${request.courseId}/availability/accounts`, {
        data: { accounts: request.accounts },
        withCredentials: true,
      });

      return new MessageResponseModel(result.data);
    } catch (e) {
      throw e as AxiosError;
    }
  };

  const getUnavailableAccounts = async (
    params: ListAccountsRequest,
    courseId?: string,
  ): Promise<ListAccountsResponse> => {
    try {
      const result = await client.get<
        ListAccountsResponse,
        AxiosResponse<ListAccountsResponse>
      >(`${COURSES_BASE_URL}/${courseId}/unavailable-accounts`, {
        withCredentials: true,
        params: processListingParams(params),
      });

      return result.data;
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const getCourseSchedules = async (
    params: ListCourseScheduleRequest,
    courseId?: string,
  ): Promise<ListCourseScheduleResponse> => {
    try {
      const result = await client.get<
        ListCourseScheduleRequest,
        AxiosResponse<ListCourseScheduleResponse>
      >(`${COURSES_BASE_URL}/${courseId}/schedules`, {
        withCredentials: true,
        params: processListingParams(params),
      });

      return result.data;
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const saveAccountSchedule = async (
    request: AccountsSchedulesRequest,
  ): Promise<MessageResponseModel> => {
    try {
      const result = await client.post<
        AccountsSchedulesRequest,
        AxiosResponse<MessageResponseModel>
      >(
        `${COURSES_BASE_URL}/account-schedules`,
        {
          courses: request.courses,
          accounts: request.accounts,
          date: request.date,
          autoEnroll: request.autoEnroll,
          type: request.type,
        },
        {
          withCredentials: true,
        },
      );

      return result.data;
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const saveBranchesSchedules = async (
    request: BranchesSchedulesRequest,
  ): Promise<MessageResponseModel> => {
    try {
      const result = await client.post<
        BranchesSchedulesRequest,
        AxiosResponse<MessageResponseModel>
      >(
        `${COURSES_BASE_URL}/branch-schedules`,
        {
          courses: request.courses,
          branches: request.branches,
          date: request.date,
          autoEnroll: request.autoEnroll,
          type: request.type,
        },
        {
          withCredentials: true,
        },
      );

      return result.data;
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const saveGroupsSchedules = async (
    request: GroupsSchedulesRequest,
  ): Promise<MessageResponseModel> => {
    try {
      const result = await client.post<
        GroupsSchedulesRequest,
        AxiosResponse<MessageResponseModel>
      >(
        `${COURSES_BASE_URL}/group-schedules`,
        {
          courses: request.courses,
          groups: request.groups,
          date: request.date,
          autoEnroll: request.autoEnroll,
          type: request.type,
        },
        {
          withCredentials: true,
        },
      );

      return result.data;
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const saveUsersSchedules = async (
    request: UsersSchedulesRequest,
  ): Promise<MessageResponseModel> => {
    try {
      const result = await client.post<
        UsersSchedulesRequest,
        AxiosResponse<MessageResponseModel>
      >(
        `${COURSES_BASE_URL}/user-schedules`,
        {
          courses: request.courses,
          users: request.users,
          date: request.date,
          autoEnroll: request.autoEnroll,
          type: request.type,
        },
        {
          withCredentials: true,
        },
      );

      return result.data;
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const changeScheduleDate = async (
    request: ChangeScheduleDateRequest,
  ): Promise<MessageResponseModel> => {
    try {
      const result = await client.patch<
        MessageResponseModel,
        AxiosResponse<MessageResponseModel>
      >(
        `${COURSES_BASE_URL}/${request.scheduleType}-schedules/${request.scheduleId}`,
        {
          date: request.date,
          type: request.type,
        },
        {
          withCredentials: true,
        },
      );

      return new MessageResponseModel(result.data);
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const changeScheduleEnrollType = async (
    request: ChangeScheduleEnrollTypeRequest,
  ): Promise<MessageResponseModel> => {
    try {
      const result = await client.patch<
        MessageResponseModel,
        AxiosResponse<MessageResponseModel>
      >(
        `${COURSES_BASE_URL}/${request.scheduleType}-schedules/${request.scheduleId}`,
        { autoEnroll: request.autoEnroll, type: request.type },
        {
          withCredentials: true,
        },
      );

      return new MessageResponseModel(result.data);
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const deleteSchedule = async (
    scheduleId: string,
    scheduleType: CourseEntityScheduleEnum,
  ): Promise<MessageResponseModel> => {
    try {
      const result = await client.delete<
        MessageResponseModel,
        AxiosResponse<MessageResponseModel>
      >(`${COURSES_BASE_URL}/${scheduleType}-schedules/${scheduleId}`, {
        withCredentials: true,
      });

      return new MessageResponseModel(result.data);
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const deleteEnrollment = async (
    payload: DeleteEnrollmentPayload,
  ): Promise<MessageResponseModel> => {
    try {
      const result = await client.post<
        MessageResponseModel,
        AxiosResponse<MessageResponseModel>
      >(`${COURSES_BASE_URL}/${payload.courseId}/unenroll`, payload.request, {
        withCredentials: true,
      });

      return new MessageResponseModel(result.data);
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const getAccountSchedules = async (
    params: ListAccountScheduleRequest,
    accountId?: string,
  ): Promise<ListAccountScheduleResponse> => {
    try {
      const result = await client.get<
        ListAccountScheduleRequest,
        AxiosResponse<ListAccountScheduleResponse>
      >(`/accounts/${accountId}/course-schedules`, {
        withCredentials: true,
        params: processListingParams(params),
      });
      return result.data;
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const getCourseAccounts = async (
    params: ListCourseAccountRequest,
    courseId?: string,
  ): Promise<ListCourseAccountResponse> => {
    try {
      const result = await client.get<
        ListCourseAccountRequest,
        AxiosResponse<ListCourseAccountResponse>
      >(`${COURSES_BASE_URL}/${courseId}/accounts`, {
        withCredentials: true,
        params: processListingParams(params),
      });
      return result.data;
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  /**
   * @deprecated
   */
  const getCourseBranches = async (
    params: ListCourseBranchesRequest,
    courseId?: string,
    accountId?: string,
  ): Promise<ListCourseBranchesResponse> => {
    try {
      const result = await client.get<
        ListCourseBranchesRequest,
        AxiosResponse<ListCourseBranchesResponse>
      >(`accounts/${accountId}/courses/${courseId}/branches`, {
        withCredentials: true,
        params: processListingParams(params),
      });
      return result.data;
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  /**
   * @deprecated
   */
  const getCourseGroups = async (
    params: ListCourseGroupsRequest,
    courseId?: string,
    accountId?: string,
  ): Promise<ListCourseGroupsResponse> => {
    try {
      const result = await client.get<
        ListCourseGroupsRequest,
        AxiosResponse<ListCourseGroupsResponse>
      >(`accounts/${accountId}/courses/${courseId}/groups`, {
        withCredentials: true,
        params: processListingParams(params),
      });
      return result.data;
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const getCourseUsers = async (
    params: ListCourseUsersRequest,
    courseId?: string,
    accountId?: string,
  ): Promise<ListCourseUsersResponse> => {
    try {
      const result = await client.get<
        ListCourseUsersRequest,
        AxiosResponse<ListCourseUsersResponse>
      >(`accounts/${accountId}/courses/${courseId}/users`, {
        withCredentials: true,
        params: processListingParams(params),
      });
      return result.data;
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const getCourseUsersSummary = async (
    params: CourseUsersSummaryRequest,
    courseId?: string,
    accountId?: string,
  ): Promise<CourseUsersSummary> => {
    try {
      const result = await client.get<
        CourseUsersSummaryRequest,
        AxiosResponse<CourseUsersSummary>
      >(`accounts/${accountId}/courses/${courseId}/users/summary`, {
        withCredentials: true,
        params: processListingParams(params),
      });
      return result.data;
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const coursesNotifyUrl = (courseId: string) =>
    `${client.defaults.baseURL}courses/notify/${courseId}`;

  const forceComplete = async (
    request: ForceCompleteRequest,
  ): Promise<MessageResponseModel> => {
    try {
      const result = await client.post<
        ForceCompleteRequest,
        AxiosResponse<MessageResponseModel>
      >(
        `accounts/${request.accountId}/courses/${request.courseId}/users/${request.userId}/force-complete`,
        { score: request.score },
        { withCredentials: true },
      );

      return result.data;
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const getCourseQuiz = async (
    courseId: string,
    quizId: string,
  ): Promise<Quiz> => {
    try {
      const result = await client.get<Quiz, AxiosResponse<Quiz>>(
        `${COURSES_BASE_URL}/${courseId}/quizzes/${quizId}`,
        {
          withCredentials: true,
        },
      );
      return result.data;
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const createCourseResource = async (
    courseId: string,
    resource: Resource,
  ): Promise<Resource> => {
    try {
      const result = await client.post<any, AxiosResponse<Resource>>(
        `${COURSES_BASE_URL}/${courseId}/resources`,
        resource,
        {
          withCredentials: true,
        },
      );
      return result.data;
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const getCourseResource = async (
    courseId: string,
    resourceId: string,
  ): Promise<Resource> => {
    try {
      const result = await client.get<string, AxiosResponse<Resource>>(
        `${COURSES_BASE_URL}/${courseId}/resources/${resourceId}`,
        {
          withCredentials: true,
        },
      );
      return result.data;
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const updateCourseResource = async (
    courseId: string,
    resourceId: string,
    resource: Resource,
  ): Promise<Resource> => {
    try {
      const result = await client.put<Resource, AxiosResponse<Resource>>(
        `${COURSES_BASE_URL}/${courseId}/resources/${resourceId}`,
        resource,
        {
          withCredentials: true,
        },
      );
      return result.data;
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const createSlideDeck = async (
    courseId: string,
    slideDeck: SlideDeck,
  ): Promise<SlideDeck> => {
    try {
      const result = await client.post<any, AxiosResponse<SlideDeck>>(
        `${COURSES_BASE_URL}/${courseId}/presentations`,
        slideDeck,
        {
          withCredentials: true,
        },
      );
      return result.data;
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const getSlideDeck = async (
    courseId: string,
    slideDeckId: string,
  ): Promise<SlideDeck> => {
    try {
      const result = await client.get<any, AxiosResponse<SlideDeck>>(
        `${COURSES_BASE_URL}/${courseId}/presentations/${slideDeckId}`,
        {
          withCredentials: true,
        },
      );
      return result.data;
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const updateSlideDeck = async (
    courseId: string,
    slideDeckId: string,
    slideDeck: SlideDeck,
  ): Promise<SlideDeck> => {
    try {
      const result = await client.put<any, AxiosResponse<SlideDeck>>(
        `${COURSES_BASE_URL}/${courseId}/presentations/${slideDeckId}`,
        slideDeck,
        {
          withCredentials: true,
        },
      );
      return result.data;
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const getHighlightedCourses = async (
    accountId?: string,
  ): Promise<HighlightedCourse[]> => {
    try {
      const result = await client.get<
        ListCoursesRequest,
        AxiosResponse<HighlightedCourse[]>
      >(`accounts/${accountId}/highlighted-courses`, {
        withCredentials: true,
      });
      return result.data;
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const exportCourseAsScorm = async (
    courseId: string,
    language: string,
    accountId: string,
  ): Promise<{ downloadUrl: string }> => {
    try {
      const result = await client.post<
        { downloadUrl: string },
        AxiosResponse<{ downloadUrl: string }>
      >(
        `${ACCOUNT_COURSES_BASE_URL(accountId)}/${courseId}/scorm-package`,
        { language },
        { withCredentials: true },
      );

      return result.data;
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const cloneCourse = async (
    accountId: string,
    courseId: string,
  ): Promise<Course> => {
    try {
      const result = await client.post<Course, AxiosResponse<Course>>(
        `accounts/${accountId}/courses/${courseId}/clone`,
        {},
        {
          withCredentials: true,
        },
      );

      return result.data;
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const customizeCourse = async (
    accountId: string,
    courseId: string,
    language?: string,
  ): Promise<Course> => {
    try {
      const result = await client.post<Course, AxiosResponse<Course>>(
        `/accounts/${accountId}/courses/${courseId}/customization`,
        language ? ({ language } as CustomizeCourseRequest) : {},
        {
          withCredentials: true,
        },
      );

      return result.data;
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const getCourseChanges = async (
    accountId: string,
    courseId: string,
  ): Promise<CourseChanges> => {
    try {
      const result = await client.get<
        CourseChanges,
        AxiosResponse<CourseChanges>
      >(`/accounts/${accountId}/courses/${courseId}/changes`, {
        withCredentials: true,
      });
      return result.data;
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const replaceScheduledContent = async (
    accountId: string,
    originalCourseId: string,
    customizedCourseId: string,
  ): Promise<MessageResponse> => {
    try {
      const result = await client.post<
        MessageResponse,
        AxiosResponse<MessageResponse>
      >(
        `/accounts/${accountId}/courses/${originalCourseId}/replace-scheduled-content`,
        { customizedCourseId },
        {
          withCredentials: true,
        },
      );

      return result.data;
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const migrateCourse = async (request: {
    oldCourseId: string;
    newCourseId: string;
    mode: MigrateCourseMode;
  }): Promise<MigrateCourseResponse> => {
    try {
      const result = await client.post<
        MigrateCourseResponse,
        AxiosResponse<MigrateCourseResponse>
      >(
        `${COURSES_BASE_URL}/migrate-course/${request.newCourseId}`,
        { oldCourseId: request.oldCourseId, mode: request.mode },
        {
          withCredentials: true,
        },
      );

      return result.data;
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  return {
    getCourses,
    getAccountCourses,
    getCourse,
    saveCourse,
    updateCourse,
    deleteCourse,
    getCourseUrl,
    getResourceDispatchUrl,
    saveAvailableAccounts,
    getUnavailableCoursesToAccount,
    saveAvailableCoursesToAccount,
    saveUnavailableAccounts,
    getUnavailableAccounts,
    getCourseSchedules,
    saveAccountSchedule,
    saveBranchesSchedules,
    saveGroupsSchedules,
    saveUsersSchedules,
    changeScheduleDate,
    changeScheduleEnrollType,
    deleteSchedule,
    deleteEnrollment,
    getAccountSchedules,
    getCourseAccounts,
    getCourseBranches,
    getCourseGroups,
    getCourseUsers,
    coursesNotifyUrl,
    forceComplete,
    getCourseQuiz,
    createCourseResource,
    getCourseResource,
    updateCourseResource,
    getHighlightedCourses,
    getCourseUsersSummary,
    createSlideDeck,
    getSlideDeck,
    updateSlideDeck,
    exportCourseAsScorm,
    cloneCourse,
    customizeCourse,
    getCourseChanges,
    replaceScheduledContent,
    migrateCourse,
  };
};
